/**
 * Cambrian shared types — creature tiers and definitions, and the
 * creature / projectile ECS components. Body parts live in parts.ts.
 *
 * Living here (vs /src/data/types.ts) so Stage 3 stays self-contained
 * and doesn't bleed types into stages 1/2.
 */

import { defineComponent } from '@/core/ECS';

// --- creatures / ecosystem ---

export type CreatureTier = 1 | 2 | 3 | 4;

export type CreatureBehavior = 'passive' | 'herd' | 'hunter' | 'apex';

export interface CreatureDef {
  id: string;
  name: string;
  tier: CreatureTier;
  hp: number;
  speed: number;
  damage: number;
  behavior: CreatureBehavior;
  /** Tiers this creature preys on. Empty for tier 1. */
  eatsTiers: CreatureTier[];
  color: number;
  size: number;
  codexId?: string;
  spriteKey?: string;
  /** Biomass + XP awarded when eaten by the player. */
  biomass: number;
  xp: number;
  /** Relative spawn odds within its tier (default 1). */
  weight?: number;
  /** Eating it heals you, or poisons you (hurts, no biomass). */
  effect?: 'heal' | 'poison';
  /** Only spawns once the player has evolved this many times. */
  minGrowth?: number;
}

// --- ECS components for Cambrian ---


export interface CreatureC {
  defId: string;
  tier: CreatureTier;
  hp: number;
  maxHp: number;
  speed: number;
  damage: number;
  behavior: CreatureBehavior;
  eatsTiers: CreatureTier[];
  color: number;
  size: number;
  /** Direction angle (radians) — for hunters/herd movement. */
  heading: number;
  /** Current hunt/wander timer. */
  ai: number;
  /** Target entity id (when hunting). 0 = none. */
  target: number;
  /** Apex flag — special handling for the apex predator. */
  isApex?: boolean;
  /** Codex id for first-encounter unlock. */
  codexId?: string;
  /** Random phase offset (radians) so a school of the same creature
   *  doesn't pulse in lockstep — keeps the swarm visually organic. */
  phase?: number;
  /** Plankton line formation (tier 1 only): which line, position in it,
   *  and the stage clock when the line formed. */
  lineId?: number;
  lineIdx?: number;
  lineT?: number;
  /** Threat AI (creatures too big for the player to bite): seconds of
   *  pursuit left, wind-up before a strike, strike burst left, and the
   *  cooldown before it can take interest again. */
  chaseT?: number;
  windup?: number;
  burst?: number;
  cool?: number;
  /** Heading owned by the player-hunt AI while chasing; reasserted each
   *  frame over whatever the ecosystem's prey-hunting/wander wrote. */
  huntHeading?: number;
  /** Prey evasion: a startled dart (velocity applied on top of swimming)
   *  and the cooldown before it can startle again. */
  dartVx?: number;
  dartVy?: number;
  startleCd?: number;
  /** Smoothed heading used for drawing, so AI heading snaps read as turns. */
  drawHeading?: number;
  /** Growth-scaled body size (1 = as defined); also scales its payout. */
  scale?: number;
  /** Stinger venom: seconds left and damage per second. */
  venomT?: number;
  venomDps?: number;
}
export const CreatureComp = defineComponent<CreatureC>('cambrian:creature');

export interface ProjectileC {
  ownerId: number;
  vx: number;
  vy: number;
  damage: number;
  lifetime: number;
  age: number;
  pierce: number;
  hitSet: Set<number>;
  color: number;
  size: number;
  kind: 'projectile' | 'pulse' | 'aura' | 'beam';
}
export const ProjectileComp = defineComponent<ProjectileC>('cambrian:projectile');
